import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { api } from '../lib/api.js';

const schema = z.object({ email: z.string().min(1), password: z.string().min(1) });
type FormData = z.infer<typeof schema>;

const forgotSchema = z.object({ email: z.string().min(1) });
type ForgotFormData = z.infer<typeof forgotSchema>;

// La landing de ventas (landing/site) no crea la solicitud: manda acá con
// `?registro=1&email=…` y la persona completa nombre, negocio y contraseña en
// este mismo formulario. Así hay UN solo camino de alta —
// POST /api/auth/signup → signup_requests → Solicitudes en Plataforma— en vez
// de una bandeja paralela que hubiera que revisar aparte.
//
// La query se borra de la barra de direcciones apenas se lee: sin eso, un F5
// o el botón de atrás vuelven a abrir el registro con el correo viejo ya
// puesto, y el correo queda a la vista en la URL más tiempo del necesario.
function leerInvitacionDeLaLanding(): { registro: boolean; email: string } {
  if (typeof window === 'undefined') return { registro: false, email: '' };
  const q = new URLSearchParams(window.location.search);
  const invitacion = { registro: q.has('registro'), email: q.get('email') ?? '' };
  if (invitacion.registro || invitacion.email) {
    q.delete('registro');
    q.delete('email');
    const resto = q.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (resto ? `?${resto}` : '') + window.location.hash,
    );
  }
  return invitacion;
}

const signupSchema = z.object({
  personName: z.string().min(1),
  businessName: z.string().min(1),
  email: z.string().min(1),
  password: z.string().min(8),
});
type SignupFormData = z.infer<typeof signupSchema>;

interface LoginPageProps {
  onSuccess: () => void;
}

// Toggle en el mismo componente en vez de una ruta separada — es un solo
// campo (email) sin token que validar, no amerita el peso de una página
// propia (a diferencia de SetPasswordPage, que sí necesita URL propia para
// el link del email).
function ForgotPasswordForm({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<ForgotFormData>({ resolver: zodResolver(forgotSchema) });

  const onSubmit = async (data: ForgotFormData) => {
    await api.post('/api/auth/forgot-password', data);
    setSent(true);
  };

  return (
    <div className="card p-8 w-full max-w-sm">
      <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--evo-cyan)' }}>
        {t('auth.forgotPasswordTitle')}
      </h1>
      <p className="text-sm mb-6" style={{ color: 'var(--evo-fg-3)' }}>
        {sent ? t('auth.forgotPasswordSent') : t('auth.forgotPasswordSubtitle')}
      </p>

      {!sent && (
        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Input
                type="email"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                autoComplete="username"
                label={t('auth.email')}
                fullWidth
              />
            )}
          />
          <Button type="submit" variant="solid" tone="cyan" fullWidth loading={isSubmitting}>
            {t('auth.forgotPasswordSubmit')}
          </Button>
        </form>
      )}

      <div style={{ marginTop: 'var(--evo-space-4)' }}>
        <Button variant="ghost" tone="muted" fullWidth onClick={onBack}>
          {t('auth.backToLogin')}
        </Button>
      </div>
    </div>
  );
}

// Registro público (2026-08-05) — misma estructura de card que login/forgot-
// password, cero CSS nuevo. Al enviar, la solicitud queda pendiente de
// aprobación — nunca entra sola, mismo patrón "sent" que ForgotPasswordForm.
function SignupForm({ onBack, emailInicial = '' }: { onBack: () => void; emailInicial?: string }) {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: { personName: '', businessName: '', email: emailInicial, password: '' },
  });

  const onSubmit = async (data: SignupFormData) => {
    await api.post('/api/auth/signup', data);
    setSent(true);
  };

  return (
    <div className="card p-8 w-full max-w-sm">
      <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--evo-cyan)' }}>
        {t('auth.signupTitle')}
      </h1>
      <p className="text-sm mb-6" style={{ color: 'var(--evo-fg-3)' }}>
        {sent ? t('auth.signupSent') : t('auth.signupSubtitle')}
      </p>

      {!sent && (
        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
          <Controller
            name="personName"
            control={control}
            render={({ field }) => (
              <Input
                type="text"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                autoComplete="name"
                label={t('auth.personName')}
                fullWidth
              />
            )}
          />
          <Controller
            name="businessName"
            control={control}
            render={({ field }) => (
              <Input
                type="text"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                autoComplete="organization"
                label={t('auth.businessName')}
                fullWidth
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Input
                type="email"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                autoComplete="username"
                label={t('auth.email')}
                fullWidth
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <Input
                type="password"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                autoComplete="new-password"
                label={t('auth.password')}
                fullWidth
              />
            )}
          />
          <Button type="submit" variant="solid" tone="cyan" fullWidth loading={isSubmitting}>
            {t('auth.signupSubmit')}
          </Button>
        </form>
      )}

      <div style={{ marginTop: 'var(--evo-space-4)' }}>
        <Button variant="ghost" tone="muted" fullWidth onClick={onBack}>
          {t('auth.backToLogin')}
        </Button>
      </div>
    </div>
  );
}

export function LoginPage({ onSuccess }: LoginPageProps) {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [invitacion] = useState(leerInvitacionDeLaLanding);
  const [showSignup, setShowSignup] = useState(invitacion.registro);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    setError('');
    try {
      await api.post('/api/auth/login', data);
      onSuccess();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'loginError';
      setError(t(`auth.${msg}`) !== `auth.${msg}` ? t(`auth.${msg}`) : t('auth.loginError'));
    }
  };

  if (showForgot) {
    return (
      <div className="flex h-full items-center justify-center" style={{ background: 'var(--evo-bg)' }}>
        <ForgotPasswordForm onBack={() => setShowForgot(false)} />
      </div>
    );
  }

  if (showSignup) {
    return (
      <div className="flex h-full items-center justify-center" style={{ background: 'var(--evo-bg)' }}>
        <SignupForm onBack={() => setShowSignup(false)} emailInicial={invitacion.email} />
      </div>
    );
  }

  return (
    <div
      className="flex h-full items-center justify-center"
      style={{ background: 'var(--evo-bg)' }}
    >
      <div className="card p-8 w-full max-w-sm">
        <h1
          className="text-2xl font-bold mb-1"
          style={{ fontFamily: 'var(--font-display)', color: 'var(--evo-cyan)' }}
        >
          Evo Hub
        </h1>
        <p className="text-sm mb-6" style={{ color: 'var(--evo-fg-3)' }}>
          {t('app.tagline')}
        </p>

        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Input
                type="email"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                autoComplete="username"
                label={t('auth.email')}
                fullWidth
              />
            )}
          />

          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <Input
                type="password"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                autoComplete="current-password"
                label={t('auth.password')}
                fullWidth
              />
            )}
          />

          {error && (
            <p className="text-xs" style={{ color: 'var(--evo-danger)' }}>
              {error}
            </p>
          )}

          <Button type="submit" variant="solid" tone="cyan" fullWidth loading={isSubmitting}>
            {t('auth.login')}
          </Button>

          <Button variant="link" tone="muted" size="sm" fullWidth onClick={() => setShowForgot(true)}>
            {t('auth.forgotPasswordLink')}
          </Button>
          <Button variant="link" tone="muted" size="sm" fullWidth onClick={() => setShowSignup(true)}>
            {t('auth.signupLink')}
          </Button>
        </form>
      </div>
    </div>
  );
}
